import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { showToast } from '@/shared/libs/toast'
import type { ITicketConfigFeature } from '@/shared/api/types/ticket-config-types'
import { Button } from '@/shared/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card'
import { BasicInfoSection } from './BasicInfoSection'
import type { TicketConfigFormValues } from './schema'
import { FeaturesSection } from './FeaturesSection'
import { EMPTY_TICKET_CONFIG_VALUES, ticketConfigFormSchema } from './schema'

interface TicketConfigFormBaseProps {
  initialValues?: TicketConfigFormValues
  initialFeatures?: ITicketConfigFeature[]
  loading?: boolean
}

interface TicketConfigCreateFormProps extends TicketConfigFormBaseProps {
  mode: 'create'
  submitting?: boolean
  onCreate: (values: TicketConfigFormValues) => Promise<void> | void
}

interface TicketConfigEditFormProps extends TicketConfigFormBaseProps {
  mode: 'edit'
  basicInfoKey?: string
  featuresKey?: string
  submittingBasicInfo?: boolean
  submittingFeatures?: boolean
  onSaveBasicInfo: (
    basicInfo: Pick<TicketConfigFormValues, 'ticketType' | 'localizedData'>,
  ) => Promise<void> | void
  onSaveFeatures: (featureIds: string[]) => Promise<void> | void
}

type TicketConfigFormProps = TicketConfigCreateFormProps | TicketConfigEditFormProps

export function TicketConfigForm(props: TicketConfigFormProps) {
  const { mode, initialValues, loading } = props
  const { t } = useTranslation()

  const form = useForm<TicketConfigFormValues>({
    resolver: zodResolver(ticketConfigFormSchema),
    defaultValues: initialValues ?? EMPTY_TICKET_CONFIG_VALUES,
  })
  const { setValue } = form
  const lastBasicInfoKeyRef = useRef<string | undefined>(undefined)
  const lastFeaturesKeyRef = useRef<string | undefined>(undefined)

  const basicInfoKey = mode === 'edit' ? props.basicInfoKey : undefined
  const featuresKey = mode === 'edit' ? props.featuresKey : undefined

  useEffect(() => {
    if (!initialValues || !basicInfoKey || lastBasicInfoKeyRef.current === basicInfoKey) {
      return
    }
    setValue('ticketType', initialValues.ticketType)
    setValue('localizedData.en.name', initialValues.localizedData.en.name)
    setValue('localizedData.en.description', initialValues.localizedData.en.description)
    setValue('localizedData.vi-south.name', initialValues.localizedData['vi-south'].name)
    setValue(
      'localizedData.vi-south.description',
      initialValues.localizedData['vi-south'].description,
    )
    lastBasicInfoKeyRef.current = basicInfoKey
  }, [initialValues, basicInfoKey, setValue])

  useEffect(() => {
    if (!initialValues || !featuresKey || lastFeaturesKeyRef.current === featuresKey) {
      return
    }
    setValue('ticketConfigFeatureIds', initialValues.ticketConfigFeatureIds)
    lastFeaturesKeyRef.current = featuresKey
  }, [initialValues, featuresKey, setValue])

  async function handleCreate(values: TicketConfigFormValues) {
    if (mode !== 'create') return
    await props.onCreate(values)
  }

  async function handleSaveBasicInfo() {
    if (mode !== 'edit') return
    const isValid = await form.trigger([
      'ticketType',
      'localizedData.en.name',
      'localizedData.vi-south.name',
    ])
    if (!isValid) {
      showToast.error(
        t('common.requiredFieldsError', { defaultValue: 'Please fill required fields' }),
      )
      if (form.getFieldState('localizedData.en.name').invalid) {
        form.setFocus('localizedData.en.name')
        return
      }
      if (form.getFieldState('localizedData.vi-south.name').invalid) {
        form.setFocus('localizedData.vi-south.name')
        return
      }
      form.setFocus('ticketType')
      return
    }
    await props.onSaveBasicInfo({
      ticketType: form.getValues('ticketType'),
      localizedData: form.getValues('localizedData'),
    })
  }

  async function handleSaveFeatures() {
    if (mode !== 'edit') return
    await props.onSaveFeatures(form.getValues('ticketConfigFeatureIds'))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <FormProvider {...form}>
      {mode === 'create' ? (
        <form
          className="space-y-6"
          onSubmit={(event) => {
            void form.handleSubmit(handleCreate)(event)
          }}
        >
          <Card className="bg-white rounded-2xl border border-zinc-200">
            <CardHeader>
              <CardTitle className="text-base">
                {t('ticketConfig.steps.basicInfo', { defaultValue: 'Basic Info' })}
              </CardTitle>
              <CardDescription>
                {t('ticketConfig.basicInfoSubtitle', {
                  defaultValue: 'Define ticket type and localized names.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BasicInfoSection />
            </CardContent>
          </Card>

          <Card className="bg-white rounded-2xl border border-zinc-200">
            <CardHeader>
              <CardTitle className="text-base">
                {t('ticketConfig.steps.features', { defaultValue: 'Features' })}
              </CardTitle>
              <CardDescription>
                {t('ticketConfig.featuresSubtitle', {
                  defaultValue: 'Select the features included in this ticket config.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeaturesSection initialSelectedIds={initialValues?.ticketConfigFeatureIds} />
            </CardContent>
            <CardFooter className="justify-end border-t border-zinc-100 pt-4">
              <Button type="submit" disabled={props.submitting}>
                {props.submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('ticketConfig.create', { defaultValue: 'Create Ticket Config' })}
              </Button>
            </CardFooter>
          </Card>
        </form>
      ) : (
        <div className="space-y-6">
          <Card className="bg-white rounded-2xl border border-zinc-200">
            <CardHeader>
              <CardTitle className="text-base">
                {t('ticketConfig.steps.basicInfo', { defaultValue: 'Basic Info' })}
              </CardTitle>
              <CardDescription>
                {t('ticketConfig.basicInfoSubtitle', {
                  defaultValue: 'Define ticket type and localized names.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BasicInfoSection />
            </CardContent>
            <CardFooter className="justify-end border-t border-zinc-100 pt-4">
              <Button
                type="button"
                disabled={props.submittingBasicInfo}
                onClick={() => void handleSaveBasicInfo()}
              >
                {props.submittingBasicInfo && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </CardFooter>
          </Card>

          <Card className="bg-white rounded-2xl border border-zinc-200">
            <CardHeader>
              <CardTitle className="text-base">
                {t('ticketConfig.steps.features', { defaultValue: 'Features' })}
              </CardTitle>
              <CardDescription>
                {t('ticketConfig.featuresSubtitle', {
                  defaultValue: 'Select the features included in this ticket config.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeaturesSection
                initialSelectedIds={initialValues?.ticketConfigFeatureIds}
                initialFeatures={props.initialFeatures}
                selectionKey={featuresKey}
              />
            </CardContent>
            <CardFooter className="justify-end border-t border-zinc-100 pt-4">
              <Button
                type="button"
                disabled={props.submittingFeatures}
                onClick={() => void handleSaveFeatures()}
              >
                {props.submittingFeatures && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </FormProvider>
  )
}
